import { type AnalyticsEventPayload } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { type CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import { type SendMessageSearchArtifact } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type ContentContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

import { getChatContextContentType } from './getChatContextContentType';
import { getCreationContextBlocks } from './getCreationContextBlocks';
import { getCreationContextExperience } from './getCreationContextExperience';
import { handleEditorContextAnalytics } from './handleEditorContextAnalytics';
import type { ChatStoreContainerProps } from './types';

type ChatContextStoreActions = ChatStoreContainerProps['chatContextStoreActions'];

/**
 * Result shape produced by {@link populateSendMessageContext}. Field types are
 * derived directly from the chat-context store getters so the concrete context
 * shapes (e.g. `editorContext.isViewMode`, `editorContext.dynamicUiType`) are
 * preserved for downstream consumers. Made explicit to satisfy
 * `--isolatedDeclarations` now that this helper is exported from its own file.
 */
type PopulateSendMessageContextResult = {
	browserContext: Awaited<ReturnType<ChatContextStoreActions['getBrowserContext']>>;
	editorContext: Awaited<ReturnType<ChatContextStoreActions['getEditorContext']>> | undefined;
	whiteboardContext:
		| Awaited<ReturnType<ChatContextStoreActions['getWhiteboardContext']>>
		| undefined;
	slidesContext: Awaited<ReturnType<ChatContextStoreActions['getSlidesContext']>> | undefined;
	databaseContext: Awaited<ReturnType<ChatContextStoreActions['getDatabaseContext']>> | undefined;
	dashboardEditorContext: Awaited<ReturnType<ChatContextStoreActions['getDashboardEditorContext']>>;
	additionalContext: {
		spaceCreateContext: Awaited<ReturnType<ChatContextStoreActions['getSpaceCreateContext']>>;
		creationContext: Record<string, unknown>;
		userTimezone: string;
		search_artifact: SendMessageSearchArtifact | undefined;
	};
	aiFeatureContext: ReturnType<ChatContextStoreActions['getAIFeatureContext']>;
	jiraWorkflowContext: ReturnType<ChatContextStoreActions['getJiraWorkflowContext']>;
	dashboardContext: ReturnType<ChatContextStoreActions['getDashboardContext']>;
	useCurrentBrowsingContext: ReturnType<ChatContextStoreActions['useCurrentBrowsingContext']>;
};

export const populateSendMessageContext = async (
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'],
	isFirstPrompt: boolean,
	source: string | undefined,
	addCurrentPageAsContextUserPreference: boolean | undefined,
	contentContextValue: ContentContext,
	creationContextParams: CreationContextParams | undefined,
	productKey: string | undefined,
	sendAnalyticsEvent: (payload: AnalyticsEventPayload) => void,
	activeTargetId?: string | number,
	lastAssistantMessageDynamicUiType?: string,
	skipEditorContext?: boolean,
	isRemixAction?: boolean,
	searchArtifact?: SendMessageSearchArtifact,
): Promise<PopulateSendMessageContextResult> => {
	const editorContext = skipEditorContext
		? undefined
		: await chatContextStoreActions.getEditorContext({
				isFirstPrompt,
				source,
				contentContext: contentContextValue,
				addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
				activeTargetId,
			});

	const currentTarget = chatContextStoreActions.getTarget(contentContextValue, activeTargetId);

	// If there's no editor context available and the current target CAN
	// provide context, send analytics to track this error state
	if (!editorContext && !skipEditorContext && currentTarget?.canProvideContext) {
		handleEditorContextAnalytics({
			isFirstPrompt,
			source,
			contentContext: contentContextValue,
			userPreference: !!addCurrentPageAsContextUserPreference,
			sendAnalyticsEvent,
			activeTargetId: !!activeTargetId,
			isStagingArea: chatContextStoreActions.getIsStagingArea(),
		});
	}

	const whiteboardContext = await chatContextStoreActions.getWhiteboardContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const slidesContext = await chatContextStoreActions.getSlidesContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const databaseContext = await chatContextStoreActions.getDatabaseContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const dashboardEditorContext = await chatContextStoreActions.getDashboardEditorContext({
		contentContext: contentContextValue,
		activeTargetId,
	});

	const browserContext = await chatContextStoreActions.getBrowserContext({
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		enabled: true,
	});

	const jiraContext = await (async () => {
		// Only fetch Jira context when in staging area and product is Jira
		// and not the first prompt (refinement messages)
		if (
			!isFirstPrompt &&
			contentContextValue === 'staging-area' &&
			productKey === 'jira' &&
			fg('jira_aifc_enabled')
		) {
			return chatContextStoreActions.getJiraContext?.();
		}
		return undefined;
	})();

	const additionalCreationContext = editorContext?.additionalContext;
	const additionalCreationContextParams = creationContextParams?.additionalContext;
	if (editorContext && editorContext.additionalContext) {
		delete editorContext.additionalContext;
	}
	const useGenericEditorSkill = editorContext?.useGenericEditorSkill;
	if (editorContext && 'useGenericEditorSkill' in editorContext) {
		delete editorContext.useGenericEditorSkill;
	}

	const isViewMode =
		contentContextValue === 'staging-area'
			? !!editorContext?.isViewMode
			: !!(
					editorContext?.isViewMode ||
					whiteboardContext?.isViewMode ||
					slidesContext?.isViewMode ||
					databaseContext?.isViewMode
				);

	// On a Confluence page in view mode, the editor bridge is not active so editorContext
	// is undefined. We detect this case by checking that productKey is 'confluence', that
	// skipEditorContext is false (we did try to fetch context), and that no other Confluence
	// doc-type context (whiteboard, slides, database) is present — meaning this is a page.
	const isConfluencePageViewMode =
		productKey === 'confluence' &&
		!skipEditorContext &&
		!whiteboardContext &&
		!slidesContext &&
		!databaseContext
			? editorContext === undefined
			: false;
	const creationContextIsViewMode = expValEquals('cc-maui-experiment', 'isEnabled', true)
		? creationContextParams?.isViewMode !== undefined
			? creationContextParams.isViewMode
			: isViewMode || isConfluencePageViewMode
		: isViewMode;
	const isDraftLockedForEditing = !!editorContext?.isDraftLockedForEditing;
	const editorContentType = getChatContextContentType({
		editorContext,
		whiteboardContext,
		databaseContext,
		slidesContext,
	});
	const contentMauiId = editorContext?.contentMauiId ?? creationContextParams?.contentMauiId;
	const mediaFileId = editorContext?.mediaFileId ?? creationContextParams?.mediaFileId;
	// The collection id for the selected media node, sent so the backend can resolve the
	// current media's collection when editing an existing image (image-create action).
	// Only populated for the editor/page flow via read-content-command.
	const mediaCollection = editorContext?.mediaCollection;
	const creationContextSource =
		contentMauiId || mediaFileId
			? 'remix'
			: // If source is explicitly passed, use that, otherwise rely on creationContextParams.source which is set at creation entry point (e.g. 'remix', 'ai-first-creation')
				(source ?? creationContextParams?.source);
	const isRemixExperienceContextEnabled =
		expValEquals('rovo_remix_experience_context', 'isEnabled', true) ||
		// `rovo_remix_experience_context` only targets confluence_web; enable the remix
		// experience for the Jira remix-edit path via its own rollout flag.
		(productKey !== 'confluence' && fg('platform_editor_maui_jira_remix_edit'));
	// The context store returns the raw content context; the outbound
	// creationContext experience/contentTypes are attached here when the request is built.
	const creationContextExperience = getCreationContextExperience({
		contentContextValue,
		isFirstPrompt,
		source,
		creationContextSource: creationContextParams?.source,
		existingExperience: creationContextParams?.experience,
		existingContentTypes: creationContextParams?.contentTypes,
		chatContextContentType: editorContentType,
		isRemixAction,
		isViewMode: creationContextIsViewMode,
		contentMauiId,
		mediaFileId,
		isRemixExperienceContextEnabled,
	});
	const creationContextBlocks = getCreationContextBlocks({
		existingBlocks: creationContextParams?.blocks,
		experience: creationContextExperience?.experience ?? creationContextParams?.experience,
		source: creationContextSource,
		mediaFileId,
		isRemixExperienceContextEnabled,
	});
	// Bookmark
	return {
		browserContext,
		editorContext,
		whiteboardContext,
		slidesContext,
		databaseContext,
		dashboardEditorContext,
		additionalContext: {
			spaceCreateContext: await chatContextStoreActions.getSpaceCreateContext({
				isFirstPrompt,
				contentContext: contentContextValue,
			}),
			creationContext: {
				...(creationContextParams ? creationContextParams : {}),
				isStagingArea: contentContextValue === 'staging-area',
				productKey,
				// When `contentMauiId` or `mediaFileId` is present, we don't need to send the source
				// Ideally we want to set it upstream, but we need to ensure we don't break existing functionality
				source: creationContextSource,
				isViewMode: creationContextIsViewMode,
				isDraftLockedForEditing,
				...(creationContextIsViewMode &&
				editorContext?.isViewMode &&
				!creationContextParams?.experience
					? {
							experience: 'inline_view' as const,
							contentTypes: ['page'],
						}
					: {}),
				useGenericEditorSkill,
				additionalContext:
					additionalCreationContext || additionalCreationContextParams || mediaCollection
						? {
								...additionalCreationContext,
								...additionalCreationContextParams,
								...(mediaCollection ? { mediaCollection } : {}),
							}
						: undefined,
				...(jiraContext && { jiraContext }),
				...(expValEquals('cc-maui-experiment', 'isEnabled', true) && {
					...(editorContext?.dynamicUiSource && {
						dynamicUiSource: {
							...editorContext.dynamicUiSource,
							...(creationContextParams?.dynamicUiSource?.fragmentAdf && {
								fragmentAdf: creationContextParams.dynamicUiSource.fragmentAdf,
							}),
						},
					}),
					...((isRemixAction ||
						source === 'remix' ||
						creationContextParams?.source === 'remix') && {
						dynamicUiType:
							editorContext?.dynamicUiType ||
							creationContextParams?.dynamicUiType ||
							lastAssistantMessageDynamicUiType,
					}),
					contentMauiId,
					mediaFileId,
				}),
				...(creationContextBlocks && { blocks: creationContextBlocks }),
				// Derive experience and contentTypes for CWR staging-area follow-ups and global chat context.
				...creationContextExperience,
			},
			userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			search_artifact: searchArtifact,
		},
		aiFeatureContext: chatContextStoreActions.getAIFeatureContext(),
		jiraWorkflowContext: chatContextStoreActions.getJiraWorkflowContext(),
		dashboardContext: chatContextStoreActions.getDashboardContext(),
		useCurrentBrowsingContext: chatContextStoreActions.useCurrentBrowsingContext(
			!!addCurrentPageAsContextUserPreference,
		),
	};
};
